'use client'

import React, { createContext, useState, useCallback, ReactNode } from 'react'
import {
  Toast,
  ToastProvider as RadixToastProvider,
  ToastType,
  type ToastProps,
} from 'sidecar-components/molecules/Toast'
import { Sentiment } from 'sidecar-components/types'

/** Interface for individual toast items in the queue */
interface ToastItem {
  id: string
  content: string
  sentiment: Sentiment
  open: boolean
  duration: number
}

interface ToastContextType {
  showToast: (content: string, sentiment?: Sentiment) => void
}

/** Toast context for managing toast notifications throughout the app */
export const ToastContext = createContext<ToastContextType | undefined>(
  undefined,
)

interface ToastProviderProps {
  children: ReactNode
}

/**
 * ToastProvider component that manages toast notification state and rendering.
 *
 * This provider:
 *
 * - Wraps the app with Radix UI ToastProvider
 * - Manages a queue of toast notifications
 * - Provides showToast function via context
 * - Handles auto-dismiss and manual dismiss logic
 * - Renders Toast component from sidecar-components
 *
 * Usage:
 *
 * ```tsx
 * const { showToast } = useToast()
 * showToast('Message here', Sentiment.ACCENT)
 * ```
 */
export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastItem[]>([])

  const showToast = useCallback(
    (content: string, sentiment = Sentiment.ACCENT) => {
      // Generate unique ID for this toast
      const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
      const duration = sentiment === Sentiment.RISK ? 7000 : 5000

      const newToast: ToastItem = {
        id,
        content,
        sentiment,
        open: true,
        duration,
      }

      // Add toast to queue
      setToasts(prev => [...prev, newToast])

      // Clean up React state after duration (matches Radix auto-dismiss)
      setTimeout(() => {
        setToasts(prev => prev.filter(t => t.id !== id))
      }, duration)
    },
    [],
  )

  /** Handles toast close events (manual dismiss or auto-dismiss) */
  const handleOpenChange = useCallback((id: string, open: boolean) => {
    if (!open) {
      // Remove toast from queue when closed
      setToasts(prev => prev.filter(t => t.id !== id))
    }
  }, [])

  // Map our toast items to the format expected by sidecar-components Toast
  const toastProps: ToastProps[] = toasts.map(toast => ({
    id: toast.id,
    content: toast.content,
    sentiment: toast.sentiment,
    type: ToastType.FOREGROUND,
    open: toast.open,
    duration: toast.duration,
    onOpenChange: (open: boolean) => handleOpenChange(toast.id, open),
  }))

  return (
    <ToastContext.Provider value={{ showToast }}>
      <RadixToastProvider
        duration={5000}
        label="Notifications"
        swipeDirection="down"
      >
        {children}
        {/* Render Toast component only if there are toasts */}
        {toasts.length > 0 && <Toast toasts={toastProps} />}
      </RadixToastProvider>
    </ToastContext.Provider>
  )
}
